import useSWR from "swr";
import { TFunction, useTranslation } from "react-i18next";
import apiRequest from "utils/apiRequestWithErrorCode";
import useSecurityItems from "pages/Rules/hooks/useSecurityItems";
import useStorageNotificationTemplates from "pages/Rules/hooks/useStorageNotificationTemplates";
import useVariables from "utils/useVariables";
import { attachmentSizeOpts } from "pages/Rules/Form/Filters/Action/utils";
import { getCurrentPathFromLocation } from "pages/Rules/Form/Steps/StorageFilepath/utils";

import { Storage } from "@dashboard-v3/api";
import { Rule } from "pages/Rules/common/types";
import { getCloudStorage } from "pages/Rules/Form/context/ruleSelectors";

type NamedItem = { id: string; name: string };
type Direction = { type: string; value?: string };

const ROLE_KEY: Record<string, string> = {
  CO_OWNER: "storageFilepath.roles.options.CO_OWNER",
  EDITOR: "storageFilepath.roles.options.EDITOR",
  VIEWER: "storageFilepath.roles.options.VIEWER",
  PREVIEWER: "storageFilepath.roles.options.PREVIEWER",
};
/**
 * Fetches the datasets a value may need to be readable — policies, groups,
 * templates, stores — and exposes one resolver per `{ resolve }` format name.
 *
 * `enabled` gates the requests: they only run while the dialog is open.
 */
export default function useResolvers(rule: Rule, enabled: boolean) {
  const { t } = useTranslation([
    "rules",
    "common",
    "orgAndDomains",
    "fromToSelector",
  ]);
  const { securityItems } = useSecurityItems();
  const { getTemplateNameById } = useStorageNotificationTemplates();
  const { mapVarToLabel, variableOptions, userVariables } = useVariables(rule);
  const provider = getCloudStorage(rule)?.provider;
  const when = (key: string) => (enabled ? key : null);

  const { data: filterPolicies } = useSWR(when("/filter-policies"), () =>
    apiRequest<NamedItem[]>("GET", "/filter-policies")
  );
  const { data: copyPolicies } = useSWR(when("/copy-policies?limit=1000"), () =>
    apiRequest<NamedItem[]>("GET", "/copy-policies?limit=1000")
  );
  const { data: groups } = useSWR(when("/groups?limit=1000"), () =>
    apiRequest<NamedItem[]>("GET", "/groups?limit=1000")
  );
  const { data: stores } = useSWR(
    enabled && provider ? `/stores?provider=${provider}` : null,
    () =>
      apiRequest<{ id: string; label: string }[]>(
        "GET",
        `/stores?provider=${provider}`
      )
  );
  const { data: templateDefs } = useSWR(when("/template-definitions"), () =>
    apiRequest<NamedItem[]>("GET", "/template-definitions")
  );
  const { data: templatePolicies } = useSWR(when("/template-policies"), () =>
    apiRequest<NamedItem[]>("GET", "/template-policies")
  );

  function findNameById(list: NamedItem[] | undefined, id: unknown) {
    return list?.find(i => i.id === id)?.name ?? String(id);
  }

  function formatDirection(dir: Direction | undefined): string {
    if (!dir) return "";
    if (dir.type === "GROUP") return findNameById(groups, dir.value);
    if (["DOMAIN", "TAG", "EMAIL"].includes(dir.type)) return dir.value ?? "";

    return t(`fromToSelector:option.${dir.type}`);
  }

  const resolvers: Record<string, (value: unknown) => string> = {
    locale: v => t(`orgAndDomains:org.${v}`),
    template: v => String(getTemplateNameById(v)),
    filterPolicy: v => findNameById(filterPolicies, v),
    copyPolicy: v => findNameById(copyPolicies, v),
    variable: v => userVariables?.find(u => u.id === v)?.name ?? String(v),

    /** Organization accounts have no `userId`; individual ones are named by it. */
    storageAccount: v => {
      const { provider, userId } = (v ?? {}) as {
        provider?: string;
        userId?: string;
      };
      return userId || t(`cloudStorage.accounts.org.${provider}`);
    },

    filepath: v =>
      mapVarToLabel(
        getCurrentPathFromLocation(v as Storage["location"]) ?? "",
        variableOptions
      ),

    securityItem: v => {
      const id = typeof v === "object" && v ? (v as { id: string }).id : v;
      return securityItems?.find(s => s.id === id)?.name ?? String(id);
    },

    store: v => stores?.find(s => s.id === v)?.label ?? String(v),

    metadataTemplate: v => {
      const id = (v as { id?: string })?.id ?? v;
      return findNameById(
        [...(templateDefs ?? []), ...(templatePolicies ?? [])],
        id
      );
    },

    folderSharing: v => {
      const attachments = (v as { attachments?: number })?.attachments;
      if (attachments === 1) return t("cloudStorage.linkToFolder.optionAlways");
      if (attachments && attachments > 1) {
        return t("cloudStorage.linkToFolder.optionNattachments");
      }
      return t("cloudStorage.linkToFolder.optionNever");
    },

    attachmentSize: v => {
      const { size, format } = (v ?? {}) as {
        size?: number;
        format?: string;
      };
      // The select writes these unit labels by hand; reuse them verbatim.
      const unit = attachmentSizeOpts.find(o => o.value === format)?.text ?? "";
      return `${size ?? ""} ${unit}`.trim();
    },

    direction: v => formatDirection(v as Direction),

    fromTo: v => {
      const { fromDirection, toDirection } = (v ?? {}) as {
        fromDirection?: Direction;
        toDirection?: Direction;
      };
      return t("confirmRuleUpdates.targetFilter.exclusionsValue", {
        from: formatDirection(fromDirection),
        fromType: formatDirectionType(fromDirection, t),
        to: formatDirection(toDirection),
        toType: formatDirectionType(toDirection, t),
        interpolation: { escapeValue: false },
      });
    },

    supervisor: v => {
      const { entity, type } = (v ?? {}) as {
        entity?: string;
        type?: string;
      };
      if (type === "GROUP") return findNameById(groups, entity);
      if (type === "VARIABLE") {
        return userVariables?.find(u => u.id === entity)?.name ?? entity ?? "";
      }
      return entity ?? "";
    },

    collaboration: v => {
      const { position, role } = (v ?? {}) as {
        position?: number;
        role?: string;
      };
      const roleLabel = role ? t(ROLE_KEY[role] ?? role) : "";
      return position ? `${roleLabel} (#${position})` : roleLabel;
    },
  };

  return {
    resolvers,
    mapVarToLabel: (s: string) => mapVarToLabel(s, variableOptions),
  };
}

function formatDirectionType(dir: Direction | undefined, t: TFunction): string {
  if (!dir?.type) return "";
  return t(`common:${dir.type.toLowerCase()}`);
}
